import { useDealItemPicker } from './DealItemPicker';
import { classNames, useEffect, useState } from 'libs/react';
import { DealItemPickerOptionGroup } from './DealItemPickerOptionGroup';
import { DealItemPickerOptionTable } from './DealItemPickerOptionTable';
import { Text } from 'components/Text';
import { React } from 'libs/react';
import { FormInput } from 'components/form/FormInput';
import './DealItemPickerOptionPane.scss';
/**
 * @component DealItemPickerOptionPane
 * @since 1.0.0
 */
export function DealItemPickerOptionPane(props) {
    const { optionReplacement, primaryGroup, style } = Object.assign({ style: 'normal' }, props);
    const { options, appendOption, dealOptions, alternativeTo, initialRendering } = useDealItemPicker();
    useEffect(() => {
        // Editing an existing quote line must not re-apply catalog defaults.
        // Those defaults are not on the quote, and re-adding them drops hours
        // already set on the selected options.
        if (initialRendering !== false) {
            dealOptions.forEach((dealOpt) => {
                if (dealOpt.included || dealOpt.locked || dealOpt.proposed) {
                    if (options.find((entry) => entry.item.id == dealOpt.item.id) == null) {
                        appendOption(dealOpt.item, dealOpt.price, dealOpt.locked, dealOpt.proposed, dealOpt.included, dealOpt.quantity);
                    }
                }
            });
        }
        // create groups
        setGroups(createGroups(dealOptions !== null && dealOptions !== void 0 ? dealOptions : [], primaryGroup));
    }, [dealOptions, initialRendering]);
    const [groups, setGroups] = useState(createGroups(dealOptions !== null && dealOptions !== void 0 ? dealOptions : [], primaryGroup));
    /**
     * @function onSearch
     * @since 1.0.0
     * @hidden
     */
    function onSearch(value) {
        // filter deal options and rebuild groups
        const filteredOptions = dealOptions.filter((option) => {
            const item = option.item;
            if (item.title.toLowerCase().indexOf(value.toLowerCase()) > -1) {
                return true;
            }
            if (item.sku.toLowerCase().indexOf(value.toLowerCase()) > -1) {
                return true;
            }
            return false;
        });
        setGroups(createGroups(filteredOptions, primaryGroup));
    }
    const className = classNames('deal-item-picker-option-pane', 'deal-item-picker-option-pane--' + style, props.className);
    return (React.createElement("div", { id: "deal-item-picker-option-pane", className: className },
        optionReplacement == 'code' && (React.createElement("div", { className: "deal-item-picker-option-pane-banner" },
            React.createElement(Text, { className: "deal-item-picker-option-pane-banner-text", value: t('You can only select one option starting with the same letter.') }))),
        React.createElement("div", { className: "deal-item-picker-option-pane-heading" },
            React.createElement(Text, { className: "deal-item-picker-option-pane-title", value: t('Options') }),
            React.createElement(FormInput, { className: "deal-item-picker-option-pane-search", picto: "fas search", placeholder: t('Search'), onChange: (value) => {
                    onSearch(value);
                }, size: "small" })),
        React.createElement("div", { className: "deal-item-picker-option-pane-content" }, groups.length == 0 ? (React.createElement(Text, { value: t('No Options'), className: "deal-item-picker-option-pane-no-options" })) : (groups.map((group, index) => (React.createElement(DealItemPickerOptionGroup, { index: index, key: index, title: group.title, badge: group.items.length, collapse: index > 0 },
            React.createElement(DealItemPickerOptionTable, { values: group.items, optionReplacement: optionReplacement, display: group.display }))))))));
}
/**
 * @function createGroups
 * @since 1.0.0
 * @hidden
 */
function createGroups(items, primaryGroup) {
    const content = {};
    for (const item of items) {
        const itemGroups = item.item.groups && item.item.groups.length > 0
            ? item.item.groups
            : item.item.group
                ? [item.item.group]
                : [];
        if (itemGroups.length > 0) {
            for (const group of itemGroups) {
                if (group) {
                    if (content[group.id] == null) {
                        content[group.id] = {
                            title: group.title,
                            order: group.order,
                            display: group.display,
                            items: [],
                            count: 0
                        };
                    }
                    if (content[group.id].items.find((i) => i.item.id == item.item.id) ==
                        null) {
                        content[group.id].items.push(item);
                    }
                }
                else {
                    if (content['others'] == null) {
                        content['others'] = {
                            title: t('Others'),
                            items: [],
                            count: 0
                        };
                    }
                    if (content['others'].items.find((i) => i.item.id == item.item.id) ==
                        null) {
                        content['others'].items.push(item);
                    }
                }
            }
        }
        else {
            if (content['others'] == null) {
                content['others'] = {
                    title: t('Others'),
                    items: [],
                    count: 0
                };
            }
            if (content['others'].items.find((i) => i.item.id == item.item.id) == null) {
                content['others'].items.push(item);
            }
        }
        if (item.recommended) {
            if (content['recommended'] == null) {
                content['recommended'] = {
                    title: t('Favorites'),
                    items: [],
                    count: 0
                };
            }
            if (content['recommended'].items.find((i) => i.item.id == item.item.id) ==
                null) {
                content['recommended'].items.push(item);
            }
        }
    }
    const groups = Object.values(content);
    groups.sort((a, b) => {
        var _a, _b;
        const orderDiff = ((_a = a.order) !== null && _a !== void 0 ? _a : 0) - ((_b = b.order) !== null && _b !== void 0 ? _b : 0);
        if (orderDiff !== 0)
            return orderDiff;
        return a.title.localeCompare(b.title);
    });
    // put recommended at the top
    const recommended = groups.find((group) => group.title == t('Favorites'));
    if (recommended) {
        groups.splice(groups.indexOf(recommended), 1);
        groups.unshift(recommended);
    }
    // put others at the bottom
    const others = groups.find((group) => group.title == t('Others'));
    if (others) {
        groups.splice(groups.indexOf(others), 1);
        groups.push(others);
    }
    // put primary group at the top
    if (primaryGroup != 'none') {
        const primary = groups.find((group) => group.title == t(`${primaryGroup}`));
        if (primary) {
            groups.splice(groups.indexOf(primary), 1);
            groups.unshift(primary);
        }
    }
    return groups;
}
